import { Box, Icon, TextField as MuiTextField } from "@material-ui/core";
import { type TextFieldProps } from "@material-ui/core/TextField";
import React from "react";
import ReactInputMask from "react-input-mask";
import { Typography } from "src/components/Typography";
import { Css, Palette } from "src/Css";
import { ErrorPalette, FontSize, fontStyles } from "src/theme";
import { makeId } from "src/utils";
import { smallShrinkLabel } from "./SignInTextField.css";

interface Props
  extends Pick<
    TextFieldProps,
    | "type"
    | "placeholder"
    | "value"
    | "onChange"
    | "onKeyUp"
    | "multiline"
    | "inputRef"
    | "required"
    | "name"
    | "autoComplete"
  > {
  id?: string;
  alignment?: "left" | "center" | "right";
  disabled?: boolean;
  label: string;
  errors?: string[];
  hideError?: boolean;
  hideLabel?: boolean;
  // Always keep the label showing (i.e. a better name for shrink=true)
  fixedLabel?: boolean;
  min?: number;
  max?: number;
  size?: "small" | "regular";
  readOnly?: boolean;
  helperText?: React.ReactNode;
}

export function SignInTextField(props: Props) {
  const {
    alignment = "left",
    errors,
    disabled = false,
    type,
    label,
    hideError = false,
    hideLabel,
    fixedLabel,
    min,
    max,
    size = "regular",
    readOnly,
    helperText,
    name,
    autoComplete,
    ...others
  } = props;

  const id = props.id || makeId(label);
  const hasErrors = errors && errors.length > 0;
  const isSmall = size === "small";
  // Default to required b/c that is what most of our form fields are.
  const required = props.required === undefined ? true : props.required;

  const isTel = type === "tel";

  // This is jank but `inputProps` and `InputProps` are detected by eslint as being duplicate props,
  // so set inputProps this way.
  (others as any).inputProps = {
    "aria-label": label,
    "data-testid": id,
    style: {
      ...fontStyles({ size: isSmall ? "F10" : "F7" }),
      ...getInputAlignmentStyle(alignment),
    },
    ...(type === "tel" && { pattern: "^\\([0-9]{3}\\)\\s[0-9]{3}-[0-9]{4}$" }),
    ...(min && { min }),
    ...(max && { max }),
  };

  const textField = (
    <MuiTextField
      id={id}
      disabled={!isTel && disabled}
      type={type}
      name={name}
      autoComplete={autoComplete}
      fullWidth={true}
      required={required}
      css={Css.mt1.$}
      helperText={!hasErrors && helperText}
      // Allow hiding the label, but we'll always pass the text as aria-label.
      label={hideLabel ? "" : label}
      InputProps={{
        endAdornment: hasErrors && <Icon color="error">error</Icon>,
        disableUnderline: readOnly,
        readOnly,
        style: isSmall ? { fontSize: FontSize.PX12 } : undefined,
      }}
      InputLabelProps={{
        // Hide the required asterisk by telling the label it's not required
        required: false,
        className: isSmall ? smallShrinkLabel : undefined,
        style: {
          color: Palette.Gray600,
          ...fontStyles({ size: isSmall ? "F10" : "F8" }),
          ...getLabelAlignmentStyle(alignment),
        },
        shrink: fixedLabel !== undefined ? fixedLabel : undefined,
      }}
      FormHelperTextProps={{
        style: { fontSize: isSmall ? FontSize.PX10 : FontSize.PX12 },
      }}
      {...others}
    />
  );

  return (
    <>
      {isTel && (
        <ReactInputMask
          mask="(999) 999-9999"
          value={props.value as string}
          disabled={disabled}
          onChange={props.onChange}
        >
          {textField as any}
        </ReactInputMask>
      )}
      {!isTel && textField}
      {!hideError && (
        <Box my={1}>
          <Typography size="f14" color={ErrorPalette.DarkRed} data-testid={`${id}.errors`}>
            {hasErrors && errors && errors.map((e, i) => <div key={i}>{e}</div>)}
            {!hasErrors && <div>&nbsp;</div>}
          </Typography>
        </Box>
      )}
    </>
  );
}

/** Returns inline style for input text alignment. */
function getInputAlignmentStyle(alignment: "left" | "center" | "right"): React.CSSProperties {
  return { textAlign: alignment };
}

/** Returns inline style for label alignment, including positioning for center/right. */
function getLabelAlignmentStyle(alignment: "left" | "center" | "right"): React.CSSProperties {
  switch (alignment) {
    case "center":
      return { textAlign: "center", width: "100%", right: 0, transformOrigin: "top" };
    case "right":
      return { textAlign: "right", left: "auto", right: 0, transformOrigin: "top right" };
    default:
      return { textAlign: "left" };
  }
}
